import { useThreadRuntime } from "@assistant-ui/react";
import { ANALYTICS_EVENTS, trackEvent } from "@repo/ui/lib/analytics";
import { ChevronRight } from "lucide-react";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@repo/ui/components/ui/item";
import { AskTigerLogo } from "./AskTigerLogo";
import { AskTigerSparkle } from "./AskTigerSparkle";
import { welcomeScreenStyles as s } from "./WelcomeScreen.styles";
import type { WelcomeScreenProps } from "./WelcomeScreen.types";
import type { CategoryId } from "./CategoryView.types";
import type { CapabilityCard } from "./capabilities";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/** Card shape the welcome screen renders; capability cards normalize to this. */
interface WelcomeActionCard {
  id: string;
  label: string;
  description: string;
  category?: CategoryId;
  prompt: string;
  /** Sub-agent registry name the card belongs to, when the capability names one. */
  agent?: string;
}

/**
 * Sub-agents with a dedicated category view; their cards navigate there instead of
 * sending the prompt directly. Keyed by registry name (`CapabilityCard.agent`).
 * Every category card opens its view: only verb cards (Summarize conversation) send
 * straight away, so a category tap never sends a prompt the user didn't pick.
 */
const CATEGORY_BY_AGENT: Partial<Record<string, CategoryId>> = {
  scheduling: "oncall",
  customer_support: "support",
};

function toActionCard(card: CapabilityCard): WelcomeActionCard {
  return {
    id: card.id,
    label: card.label,
    description: card.description,
    category: CATEGORY_BY_AGENT[card.agent ?? card.id],
    prompt: card.prompt ?? card.examples?.[0] ?? card.label,
    ...(card.agent ? { agent: card.agent } : {}),
  };
}

/** Placeholders shown while the agent's cards load; matches the usual three so nothing jumps. */
const GHOST_CARD_COUNT = 3;

export function WelcomeScreen({
  userName,
  onSelectCategory,
  agentCards = [],
  cardsLoading = false,
}: WelcomeScreenProps) {
  const threadRuntime = useThreadRuntime();
  const cards = agentCards.map(toActionCard);

  const handleCardClick = (card: WelcomeActionCard, index: number) => {
    const opensCategory = Boolean(card.category && onSelectCategory);
    trackEvent(ANALYTICS_EVENTS.suggestionChipClicked, {
      source: "welcome_card",
      suggestionId: card.id,
      label: card.label,
      index,
      opensCategory,
      ...(card.category ? { category: card.category } : {}),
      ...(card.agent ? { agent: card.agent } : {}),
    });
    if (card.category && onSelectCategory) {
      onSelectCategory(card.category);
    } else {
      threadRuntime.append(card.prompt);
    }
  };

  return (
    <div className={s.root}>
      <AskTigerLogo className={s.avatar} />
      <h2 className={s.greeting}>
        {getGreeting()}
        {userName ? `, ${userName}` : ""}
      </h2>
      <p className={s.subtitle}>How can I help you today?</p>

      <div className={s.cardList} aria-busy={cardsLoading}>
        {cardsLoading &&
          Array.from({ length: GHOST_CARD_COUNT }, (_, index) => (
            <Item
              key={index}
              variant="outline"
              size="sm"
              className={s.ghostCard}
              aria-hidden="true"
              data-testid="welcome-card-ghost"
            >
              <span className={s.ghostIcon} />
              <ItemContent className={s.ghostContent}>
                <span className={s.ghostTitle} />
                <span className={s.ghostDesc} />
              </ItemContent>
            </Item>
          ))}
        {cards.map((card, index) => (
          <Item key={card.id} asChild variant="outline" size="sm" className={s.card}>
            <button onClick={() => handleCardClick(card, index)} className={s.cardBtn}>
              <span className={s.cardIcon}>
                <AskTigerSparkle size={20} />
              </span>
              <ItemContent className={s.cardContent}>
                <ItemTitle className={s.cardTitle}>{card.label}</ItemTitle>
                <ItemDescription className={s.cardDesc}>{card.description}</ItemDescription>
              </ItemContent>
              <ChevronRight className={s.cardChevron} />
            </button>
          </Item>
        ))}
      </div>
    </div>
  );
}
